/**
 * The sign-in experience: password, then the second factor (or setting it
 * up, with recovery codes), then where you are working today. One screen,
 * three steps, keyboard first.
 *
 * A first sign-in with a temporary password (2026-11-01) adds two steps
 * after the password: "Choose your own password", then "Add your email"
 * (when the account only has a placeholder), then two-step set-up. The
 * server keeps the session restricted until all of them are done.
 */
import { PRODUCT, type LocationScope } from '@velora/contracts';
import {
  officeColorIndex,
  type LoginStatus,
  type MfaMethodKind,
  type MfaSentMethod,
  type MfaSetupOptions,
  type MfaSignInOptions,
  type OfficeSummary,
  type SessionInfo,
} from '@velora/contracts/identity';
import { TURNSTILE_ACTIONS } from '@velora/contracts/turnstile';
import {
  BrandMark,
  Button,
  Field,
  Input,
  officeInitials,
  Turnstile,
  type TurnstileHandle,
} from '@velora/ui';
import {
  AlertCircle,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Layers,
  Lock,
  Mail,
  MessageSquare,
  RotateCcw,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { isDemoMode } from '../../../app/demo/mode.js';
import { greetingName } from '../../../app/names.js';
import { useTurnstileConfig } from '../../../app/public-config.js';
import {
  attemptsLeft,
  errorMessage,
  identityApi,
  officeSystemLabel,
  refusalReason,
} from '../api.js';
import type { AuthState } from '../session.js';
import { BrandHero } from './BrandHero.js';
import { OtpInput, QrCode } from './inputs.js';

export type SignInStep =
  'password' | 'verify' | 'change_password' | 'add_email' | 'enroll' | 'recovery' | 'office';

export function initialStep(state: AuthState): SignInStep {
  if (state.kind === 'mfa') return 'verify';
  if (state.kind === 'enroll') {
    if (state.peek.setupStep === 'password') return 'change_password';
    if (state.peek.setupStep === 'email') return 'add_email';
    return 'enroll';
  }
  return 'password';
}

/** The step a sign-in answer leads to (`null`: signed in, pick the office). */
export function stepForStatus(status: LoginStatus | string): SignInStep | null {
  switch (status) {
    case 'mfa_required':
      return 'verify';
    case 'password_change_required':
      return 'change_password';
    case 'email_required':
      return 'add_email';
    case 'mfa_enrollment_required':
      return 'enroll';
    default:
      return null;
  }
}

/** Two-column frame shared by sign-in and the lock screen. */
export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="idn-frame">
      <BrandHero />
      <section className="idn-panel" aria-label="Sign in">
        <div className="idn-panel__center">
          <div className="idn-card">
            {/* The hero carries the logo on wide screens; narrow ones hide the hero. */}
            <BrandMark name={PRODUCT.shortName} size={32} wordmark className="idn-card__brand" />
            {children}
          </div>
        </div>
        <footer className="idn-panel__foot">
          <span>{PRODUCT.name}</span>
          {showFictionalDataNote() ? <span>Fictional data only in this environment</span> : null}
        </footer>
      </section>
    </div>
  );
}

function Alert({ children }: { children: ReactNode }) {
  return (
    <div className="idn-alert" role="alert">
      <AlertCircle size={16} aria-hidden />
      <div>{children}</div>
    </div>
  );
}

function Steps({ step, first }: { step: SignInStep; first: boolean }) {
  // A first sign-in shows its account step (new password, email) too.
  const order = first
    ? (['password', 'account', 'verify', 'office'] as const)
    : (['password', 'verify', 'office'] as const);
  const at =
    step === 'enroll' || step === 'recovery'
      ? 'verify'
      : step === 'change_password' || step === 'add_email'
        ? 'account'
        : step;
  const idx = (order as readonly string[]).indexOf(at);
  const labels = { password: 'Sign in', account: 'Account', verify: 'Verify', office: 'Office' };
  return (
    <ol className="idn-steps" aria-label="Sign-in progress">
      {order.map((s, i) => (
        <li
          key={s}
          className="idn-step"
          data-state={i < idx ? 'done' : i === idx ? 'active' : 'todo'}
          aria-current={i === idx ? 'step' : undefined}
        >
          <span className="idn-step__n">{i < idx ? <Check size={12} aria-hidden /> : i + 1}</span>
          <span className="idn-step__label">{labels[s]}</span>
        </li>
      ))}
    </ol>
  );
}

function Who({ name, detail, onBack }: { name: string; detail: string; onBack?: () => void }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="idn-who">
      <span className="idn-who__avatar" aria-hidden>
        {initials}
      </span>
      <div className="idn-who__text">
        <div className="idn-who__name">{name}</div>
        <div className="idn-who__detail">{detail}</div>
      </div>
      {onBack ? (
        <button type="button" className="idn-link idn-link--muted" onClick={onBack}>
          Not you?
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Steps                                                                     */
/* ------------------------------------------------------------------------ */

function PasswordStep({
  onSignedIn,
}: {
  onSignedIn: (
    r: { status: string; displayName: string; practiceName: string },
    password: string,
  ) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bot = useTurnstileConfig();
  const [botToken, setBotToken] = useState<string | null>(null);
  const botWidget = useRef<TurnstileHandle>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your username or email, and your password.');
      return;
    }
    if (bot?.mode === 'enforce' && bot.siteKey && !botToken) {
      setError('Checking that you are not a robot. Try again in a moment.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSignedIn(
        await identityApi.login({
          // A username or an email: the server tells them apart by the @.
          email: email.trim().toLowerCase(),
          password,
          ...(botToken ? { turnstileToken: botToken } : {}),
        }),
        password,
      );
    } catch (err) {
      setError(errorMessage(err, 'Sign-in failed. Try again.'));
      setPassword('');
    } finally {
      setBusy(false);
      // Each token works once; solve again for the next try.
      if (botToken) botWidget.current?.reset();
    }
  };

  return (
    <div className="idn-view">
      <h2 className="idn-title">Welcome back</h2>
      <p className="idn-sub">Sign in to {PRODUCT.name}.</p>
      <form className="idn-form" onSubmit={submit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Username or email">
          <Input
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="mary.smith or you@practice.com"
            prefix={<Mail size={15} aria-hidden />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="lg"
            autoFocus
          />
        </Field>
        <Field label="Password" hint={caps ? 'Caps Lock is on' : undefined}>
          <Input
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="At least 12 characters"
            prefix={<Lock size={15} aria-hidden />}
            suffix={
              <button
                type="button"
                className="idn-eye"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? 'Hide password' : 'Show password'}
              >
                {show ? <EyeOff size={15} aria-hidden /> : <Eye size={15} aria-hidden />}
              </button>
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyUp={(e) => setCaps(e.getModifierState('CapsLock'))}
            size="lg"
          />
        </Field>
        <Turnstile
          ref={botWidget}
          siteKey={bot?.siteKey ?? null}
          action={TURNSTILE_ACTIONS.login}
          appearance="always"
          onToken={setBotToken}
          className="idn-turnstile"
        />
        <Button type="submit" variant="hero" size="lg" block loading={busy}>
          Continue
        </Button>
      </form>
      <p className="idn-hint">
        Forgot your password? Ask your office manager to reset it from Settings, Users.
      </p>
    </div>
  );
}

type VerifyMode = MfaMethodKind | 'recovery';

const AUTHENTICATOR_ONLY: MfaSignInOptions = {
  defaultMethod: 'totp',
  methods: [{ kind: 'totp', hint: null }],
  recoveryCodes: true,
};

function methodIcon(kind: VerifyMode) {
  if (kind === 'sms') return <MessageSquare size={15} />;
  if (kind === 'email') return <Mail size={15} />;
  if (kind === 'recovery') return <KeyRound size={15} />;
  return <Smartphone size={15} />;
}

/** One line of "Try another way". */
function otherWayLabel(kind: VerifyMode, hint: string | null): { title: string; detail: string } {
  switch (kind) {
    case 'sms':
      return {
        title: `Text a code to ${hint ?? 'my phone'}`,
        detail: 'A 6-digit code by text message',
      };
    case 'email':
      return { title: `Email a code to ${hint ?? 'my email'}`, detail: 'A 6-digit code by email' };
    case 'recovery':
      return { title: 'Use a recovery code', detail: 'One of the codes you saved at set-up' };
    default:
      return { title: 'Use the authenticator app', detail: 'The 6-digit code in the app' };
  }
}

function VerifyStep({
  name,
  practice,
  onVerified,
  onRestart,
}: {
  name: string;
  practice: string;
  onVerified: (status: LoginStatus) => void;
  onRestart: () => void;
}) {
  const [options, setOptions] = useState<MfaSignInOptions | null>(null);
  const [mode, setMode] = useState<VerifyMode>('totp');
  const [others, setOthers] = useState(false);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  // Unchecked by default. Above the code boxes because a full code submits itself.
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<Partial<Record<MfaSentMethod, string>>>({});
  const [resendAt, setResendAt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const left = 30 - (Math.floor(now / 1000) % 30);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const send = useCallback(
    async (method: MfaSentMethod) => {
      setSending(true);
      setError(null);
      try {
        const r = await identityApi.sendMfaCode(method);
        setSentTo((m) => ({ ...m, [method]: r.hint }));
        setResendAt(Date.now() + (r.retryAfterSeconds ?? 30) * 1000);
      } catch (err) {
        if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') {
          onRestart();
          return;
        }
        setError(errorMessage(err, 'The code could not be sent. Try another way.'));
      } finally {
        setSending(false);
      }
    },
    [onRestart],
  );

  const choose = useCallback(
    (next: VerifyMode) => {
      setMode(next);
      setOthers(false);
      setCode('');
      setRecovery('');
      setError(null);
      if (next === 'sms' || next === 'email') void send(next);
    },
    [send],
  );

  // The person's methods; the default one is where the step starts.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    identityApi.mfaOptions().then(
      (o) => {
        setOptions(o);
        if (o.defaultMethod !== 'totp') choose(o.defaultMethod);
      },
      (err: unknown) => {
        if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') onRestart();
        else setOptions(AUTHENTICATOR_ONLY); // an older server: authenticator and recovery codes
      },
    );
  }, [choose, onRestart]);

  const verify = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await identityApi.verifyMfa(value, remember, mode);
      onVerified(r.status);
    } catch (err) {
      if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') {
        onRestart();
        return;
      }
      const n = attemptsLeft(err);
      setError(
        `${errorMessage(err)}${n !== null ? ` ${n} ${n === 1 ? 'try' : 'tries'} left.` : ''}`,
      );
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const o = options ?? AUTHENTICATOR_ONLY;
  const hintOf = (k: MfaSentMethod) =>
    sentTo[k] ?? o.methods.find((m) => m.kind === k)?.hint ?? null;
  const alternatives: Array<{ kind: VerifyMode; hint: string | null }> = [
    ...o.methods.filter((m) => m.kind !== mode),
    ...(o.recoveryCodes && mode !== 'recovery' ? [{ kind: 'recovery' as const, hint: null }] : []),
  ];
  const sub =
    mode === 'totp'
      ? 'Enter the 6-digit code from your authenticator app.'
      : mode === 'recovery'
        ? 'Enter one of the recovery codes you saved. Each works once.'
        : sending && !sentTo[mode]
          ? `Sending a code to ${hintOf(mode) ?? 'you'}…`
          : `We ${mode === 'sms' ? 'texted' : 'emailed'} a 6-digit code to ${hintOf(mode) ?? 'you'}. It expires in 5 minutes.`;

  return (
    <div className="idn-view">
      <h2 className="idn-title">Two-step verification</h2>
      <p className="idn-sub">{sub}</p>
      <Who name={name} detail={practice} onBack={onRestart} />
      <form
        className="idn-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void verify(mode === 'recovery' ? recovery.trim() : code);
        }}
      >
        {error ? <Alert>{error}</Alert> : null}
        <label className="idn-check">
          <input
            type="checkbox"
            checked={remember}
            disabled={busy}
            onChange={(e) => setRemember(e.target.checked)}
          />
          Remember this device for 30 days
        </label>
        {mode === 'recovery' ? (
          <Field label="Recovery code">
            <Input
              value={recovery}
              onChange={(e) => setRecovery(e.target.value)}
              placeholder="xxxxx-xxxxx"
              autoComplete="off"
              spellCheck={false}
              className="idn-mono"
              size="lg"
              autoFocus
            />
          </Field>
        ) : (
          <>
            <OtpInput
              key={mode}
              value={code}
              onChange={setCode}
              onComplete={(v) => void verify(v)}
              invalid={Boolean(error)}
              disabled={busy}
              autoFocus
              label={
                mode === 'totp'
                  ? 'Code from your authenticator app'
                  : `Code sent by ${mode === 'sms' ? 'text' : 'email'}`
              }
            />
            {mode === 'totp' ? (
              <p className="idn-countdown">
                New code in <b className="idn-num">{left}</b>s
              </p>
            ) : (
              <p className="idn-countdown">
                Didn’t get it?{' '}
                <button
                  type="button"
                  className="idn-link"
                  disabled={sending || resendIn > 0}
                  onClick={() => void send(mode)}
                >
                  <RotateCcw size={12} aria-hidden />
                  {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
                </button>
              </p>
            )}
          </>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={busy}
          disabled={mode === 'recovery' ? recovery.trim().length < 10 : code.length !== 6}
        >
          Verify
        </Button>
      </form>
      {others ? (
        <div className="idn-methods" role="group" aria-label="Other ways to sign in">
          {alternatives.map((a) => {
            const t = otherWayLabel(a.kind, a.hint);
            return (
              <button
                key={a.kind}
                type="button"
                className="idn-method"
                onClick={() => choose(a.kind)}
              >
                <span className="idn-method__icon" aria-hidden>
                  {methodIcon(a.kind)}
                </span>
                <span className="idn-method__text">
                  <b>{t.title}</b>
                  <span>{t.detail}</span>
                </span>
              </button>
            );
          })}
          <p className="idn-hint">
            <AlertCircle size={13} aria-hidden />
            None of these work? Ask your office manager to reset two-step sign-in (Settings, Users,
            Reset two-step).
          </p>
        </div>
      ) : (
        <button
          type="button"
          className="idn-link idn-link--muted idn-other-ways"
          onClick={() => setOthers(true)}
        >
          {mode === 'totp' ? 'Authenticator not working? Try another way' : 'Try another way'}
        </button>
      )}
    </div>
  );
}

/** Show / hide toggle for a password field (same control as the sign-in step). */
function EyeToggle({ show, onToggle }: { show: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="idn-eye"
      onClick={onToggle}
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? <EyeOff size={15} aria-hidden /> : <Eye size={15} aria-hidden />}
    </button>
  );
}

function ChangePasswordStep({
  name,
  temporary,
  onSaved,
  onRestart,
}: {
  name: string;
  /** The temporary password typed on the first step (absent after a reload). */
  temporary: string;
  onSaved: (status: LoginStatus) => void;
  onRestart: () => void;
}) {
  const [current, setCurrent] = useState(temporary);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const askCurrent = temporary.length === 0;
  const tooShort = password.length > 0 && password.length < 12;
  const same = password.length > 0 && password === current;
  const mismatch = again.length > 0 && again !== password;
  const ok = current.length > 0 && password.length >= 12 && !same && again === password;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await identityApi.firstSignInPassword(current, password);
      onSaved(r.status);
    } catch (err) {
      if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') {
        onRestart();
        return;
      }
      const n = attemptsLeft(err);
      setError(
        `${errorMessage(err, 'Your password was not saved. Try again.')}${n !== null ? ` ${n} ${n === 1 ? 'try' : 'tries'} left.` : ''}`,
      );
      if (n !== null && !askCurrent) setCurrent('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idn-view">
      <h2 className="idn-title">Choose your own password</h2>
      <p className="idn-sub">
        You signed in with a temporary password. Choose your own to continue. Nobody else will know
        it, not even your office manager.
      </p>
      <Who name={name} detail="First sign-in on this account" onBack={onRestart} />
      <form className="idn-form" onSubmit={save} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {askCurrent || current.length === 0 ? (
          <Field label="Temporary password">
            <Input
              type={show ? 'text' : 'password'}
              autoComplete="current-password"
              prefix={<Lock size={15} aria-hidden />}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              size="lg"
              autoFocus
            />
          </Field>
        ) : null}
        <Field
          label="New password"
          hint="At least 12 characters. A short sentence is easy to remember."
          {...(tooShort
            ? { error: 'Use at least 12 characters.' }
            : same
              ? { error: 'Choose a password different from the temporary one.' }
              : {})}
        >
          <Input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            prefix={<Lock size={15} aria-hidden />}
            suffix={<EyeToggle show={show} onToggle={() => setShow((v) => !v)} />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="lg"
            autoFocus={!askCurrent}
          />
        </Field>
        <Field
          label="Type it again"
          {...(mismatch ? { error: 'The two passwords do not match.' } : {})}
        >
          <Input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            prefix={<Lock size={15} aria-hidden />}
            value={again}
            onChange={(e) => setAgain(e.target.value)}
            size="lg"
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!ok}>
          Save password
        </Button>
      </form>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddEmailStep({
  name,
  onSaved,
  onRestart,
}: {
  name: string;
  onSaved: (status: LoginStatus) => void;
  onRestart: () => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = email.trim().toLowerCase();
  const placeholder = value.endsWith('.invalid');
  const ok = EMAIL_RE.test(value) && !placeholder;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await identityApi.firstSignInEmail(value);
      onSaved(r.status);
    } catch (err) {
      if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') {
        onRestart();
        return;
      }
      setError(errorMessage(err, 'Your email was not saved. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idn-view">
      <h2 className="idn-title">Add your email</h2>
      <p className="idn-sub">
        Your account does not have an email yet. Add your own (your work email is fine), so your
        office can reach you about your {PRODUCT.shortName} account. You still sign in with your
        username.
      </p>
      <Who name={name} detail="First sign-in on this account" onBack={onRestart} />
      <form className="idn-form" onSubmit={save} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field
          label="Your email"
          {...(email.length > 0 && !EMAIL_RE.test(value)
            ? { error: 'Enter a full email address.' }
            : placeholder
              ? { error: 'Enter your own email address.' }
              : {})}
        >
          <Input
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="you@practice.com"
            prefix={<Mail size={15} aria-hidden />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="lg"
            autoFocus
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!ok}>
          Save email
        </Button>
      </form>
    </div>
  );
}

function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

function EnrollStep({
  name,
  onConfirmed,
  onCodeInstead,
  onRestart,
  onBack,
}: {
  name: string;
  onConfirmed: (codes: string[]) => void;
  /** Set-up already finished (another device or tab): ask for the code instead of a QR. */
  onCodeInstead: () => void;
  onRestart: () => void;
  /** Back to the choice of method (text and email allowed). */
  onBack?: () => void;
}) {
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const started = useRef(false);

  // The server shows the pending code again (reload, sign in again), so a
  // code already scanned keeps working; `restart` asks for a new one.
  const load = useCallback(
    (restart: boolean) =>
      identityApi
        .startEnrollment(restart)
        .then((e) => {
          setEnrollment(e);
          setCopied(false);
          if (restart) {
            setRenewed(true);
            setError(null);
            setCode('');
          }
        })
        .catch((err: unknown) => {
          const why = refusalReason(err);
          if (why === 'mfa_required') onCodeInstead();
          else if (why === 'revoked' || why === 'no_session') onRestart();
          else setError(errorMessage(err));
        }),
    [onRestart, onCodeInstead],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load(false);
  }, [load]);

  const startOver = async () => {
    setRenewing(true);
    try {
      await load(true);
    } finally {
      setRenewing(false);
    }
  };

  const confirm = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await identityApi.confirmEnrollment(value);
      onConfirmed(r.recoveryCodes);
    } catch (err) {
      if (refusalReason(err) === 'mfa_required') {
        onCodeInstead();
        return;
      }
      if (refusalReason(err) === 'revoked') {
        onRestart();
        return;
      }
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idn-view">
      <h2 className="idn-title">Set up two-step sign-in</h2>
      <p className="idn-sub">
        Your role needs a second step. Scan this code with an authenticator app such as Microsoft
        Authenticator or Google Authenticator, then enter the 6 digits it shows.
      </p>
      <Who name={name} detail="First sign-in on this account" onBack={onRestart} />
      {error ? <Alert>{error}</Alert> : null}
      {renewed && !error ? (
        <p className="idn-sub" role="status">
          New code ready. Delete the old {PRODUCT.shortName} entry in your authenticator and scan
          this one.
        </p>
      ) : null}
      {enrollment ? (
        <div className="idn-enroll">
          <QrCode text={enrollment.otpauthUri} label="QR code for your authenticator app" />
          <div className="idn-enroll__manual">
            <span className="idn-label">Or enter this key</span>
            <code className="idn-secret">{groupSecret(enrollment.secret)}</code>
            <button
              type="button"
              className="idn-link"
              onClick={() => {
                void navigator.clipboard?.writeText(enrollment.secret).then(() => setCopied(true));
              }}
            >
              <Copy size={13} aria-hidden /> {copied ? 'Copied' : 'Copy key'}
            </button>
            <button
              type="button"
              className="idn-link"
              disabled={busy || renewing}
              onClick={() => void startOver()}
            >
              <RotateCcw size={13} aria-hidden /> Start over with a new code
            </button>
          </div>
        </div>
      ) : (
        <div className="idn-enroll idn-enroll--loading" aria-busy>
          <span className="idn-qr idn-qr--placeholder" />
        </div>
      )}
      <form
        className="idn-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void confirm(code);
        }}
      >
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={(v) => void confirm(v)}
          invalid={Boolean(error)}
          disabled={busy || !enrollment}
          label="Code from your authenticator app"
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={busy}
          disabled={code.length !== 6}
        >
          Turn on two-step sign-in
        </Button>
      </form>
      {onBack ? (
        <button type="button" className="idn-link idn-link--muted idn-other-ways" onClick={onBack}>
          Choose another way
        </button>
      ) : null}
    </div>
  );
}

/**
 * Two-step set-up at sign-in: the authenticator app, or a 6-digit code by
 * text or email when the practice allows them (Settings > Security). Only
 * the authenticator allowed (or an older server): straight to its QR code.
 */
function SetupStep({
  name,
  onConfirmed,
  onCodeInstead,
  onRestart,
}: {
  name: string;
  onConfirmed: (codes: string[]) => void;
  onCodeInstead: () => void;
  onRestart: () => void;
}) {
  const [setup, setSetup] = useState<MfaSetupOptions | null>(null);
  const [choice, setChoice] = useState<MfaMethodKind | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    identityApi.setupOptions().then(
      (o) => {
        setSetup(o);
        if (o.allowed.length <= 1) setChoice('totp');
      },
      (err: unknown) => {
        const why = refusalReason(err);
        if (why === 'mfa_required') onCodeInstead();
        else if (why === 'revoked' || why === 'no_session') onRestart();
        else setChoice('totp');
      },
    );
  }, [onCodeInstead, onRestart]);

  if (choice === 'totp') {
    return (
      <EnrollStep
        name={name}
        onRestart={onRestart}
        onCodeInstead={onCodeInstead}
        onConfirmed={onConfirmed}
        {...(setup && setup.allowed.length > 1 ? { onBack: () => setChoice(null) } : {})}
      />
    );
  }
  if (choice === 'sms' || choice === 'email') {
    return (
      <ContactSetupStep
        name={name}
        kind={choice}
        initial={choice === 'email' ? (setup?.accountEmail ?? '') : ''}
        onConfirmed={onConfirmed}
        onCodeInstead={onCodeInstead}
        onRestart={onRestart}
        onBack={() => setChoice(null)}
      />
    );
  }
  const allowed = setup?.allowed ?? [];
  const ways: Array<{ kind: MfaMethodKind; title: string; detail: string }> = [
    {
      kind: 'totp',
      title: 'Authenticator app',
      detail: 'Google or Microsoft Authenticator. Works without signal. Recommended.',
    },
    { kind: 'sms', title: 'Text message', detail: 'A 6-digit code to your mobile phone' },
    { kind: 'email', title: 'Email code', detail: 'A 6-digit code to your email' },
  ];
  return (
    <div className="idn-view">
      <h2 className="idn-title">Set up two-step sign-in</h2>
      <p className="idn-sub">
        Choose how you will get a code each time you sign in. You can add another way later in
        Settings, My sign-in.
      </p>
      <Who name={name} detail="First sign-in on this account" onBack={onRestart} />
      {setup ? (
        <div className="idn-methods" role="group" aria-label="Two-step method">
          {ways
            .filter((w) => allowed.includes(w.kind))
            .map((w) => (
              <button
                key={w.kind}
                type="button"
                className="idn-method"
                onClick={() => setChoice(w.kind)}
              >
                <span className="idn-method__icon" aria-hidden>
                  {methodIcon(w.kind)}
                </span>
                <span className="idn-method__text">
                  <b>{w.title}</b>
                  <span>{w.detail}</span>
                </span>
              </button>
            ))}
        </div>
      ) : (
        <div className="idn-enroll idn-enroll--loading" aria-busy>
          <span className="idn-qr idn-qr--placeholder" />
        </div>
      )}
    </div>
  );
}

/** Proves a mobile number or an email with a code; it then becomes the sign-in method. */
function ContactSetupStep({
  name,
  kind,
  initial,
  onConfirmed,
  onCodeInstead,
  onRestart,
  onBack,
}: {
  name: string;
  kind: MfaSentMethod;
  initial: string;
  onConfirmed: (codes: string[]) => void;
  onCodeInstead: () => void;
  onRestart: () => void;
  onBack: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const sms = kind === 'sms';
  const valid = sms
    ? value.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length === 10
    : EMAIL_RE.test(value.trim()) && !value.trim().toLowerCase().endsWith('.invalid');

  const handle = (err: unknown) => {
    const why = refusalReason(err);
    if (why === 'mfa_required') onCodeInstead();
    else if (why === 'revoked' || why === 'no_session') onRestart();
    else {
      const n = attemptsLeft(err);
      setError(
        `${errorMessage(err)}${n !== null ? ` ${n} ${n === 1 ? 'try' : 'tries'} left.` : ''}`,
      );
    }
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = sentTo
        ? await identityApi.setupContactResend(kind)
        : await identityApi.setupContact(kind, value.trim());
      setSentTo(r.hint);
      setResendAt(Date.now() + (r.retryAfterSeconds ?? 30) * 1000);
    } catch (err) {
      handle(err);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (v: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await identityApi.setupContactConfirm(kind, v);
      onConfirmed(r.recoveryCodes);
    } catch (err) {
      handle(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idn-view">
      <h2 className="idn-title">{sms ? 'Get codes by text' : 'Get codes by email'}</h2>
      <p className="idn-sub">
        {sentTo
          ? `We ${sms ? 'texted' : 'emailed'} a 6-digit code to ${sentTo}. Enter it to finish. It expires in 5 minutes.`
          : sms
            ? 'Enter your mobile number. We will text it a code now to check it, then each time you sign in.'
            : 'Enter your email. We will email it a code now to check it, then each time you sign in.'}
      </p>
      <Who name={name} detail="First sign-in on this account" onBack={onRestart} />
      {error ? <Alert>{error}</Alert> : null}
      {sentTo ? (
        <form
          className="idn-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void confirm(code);
          }}
        >
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(v) => void confirm(v)}
            invalid={Boolean(error)}
            disabled={busy}
            autoFocus
            label={`Code sent by ${sms ? 'text' : 'email'}`}
          />
          <p className="idn-countdown">
            Didn’t get it?{' '}
            <button
              type="button"
              className="idn-link"
              disabled={busy || resendIn > 0}
              onClick={() => void send()}
            >
              <RotateCcw size={12} aria-hidden />
              {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
            </button>{' '}
            <button
              type="button"
              className="idn-link idn-link--muted"
              onClick={() => {
                setSentTo(null);
                setCode('');
                setError(null);
              }}
            >
              {sms ? 'Change number' : 'Change email'}
            </button>
          </p>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={code.length !== 6}
          >
            Turn on two-step sign-in
          </Button>
        </form>
      ) : (
        <form className="idn-form" noValidate onSubmit={(e) => void send(e)}>
          <Field label={sms ? 'Mobile number' : 'Email'}>
            <Input
              type={sms ? 'tel' : 'email'}
              inputMode={sms ? 'tel' : 'email'}
              autoComplete={sms ? 'tel-national' : 'email'}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={sms ? '(989) 555-0123' : 'you@practice.com'}
              prefix={
                sms ? <MessageSquare size={15} aria-hidden /> : <Mail size={15} aria-hidden />
              }
              value={value}
              onChange={(e) => setValue(e.target.value)}
              size="lg"
              autoFocus
            />
          </Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!valid}>
            Send code
          </Button>
        </form>
      )}
      <button type="button" className="idn-link idn-link--muted idn-other-ways" onClick={onBack}>
        Choose another way
      </button>
    </div>
  );
}

function RecoveryStep({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const text = `${PRODUCT.name} recovery codes\nEach code works once.\n\n${codes.join('\n')}\n`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${PRODUCT.codeName}-recovery-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setSaved(true);
  };
  return (
    <div className="idn-view">
      <h2 className="idn-title">Save your recovery codes</h2>
      <p className="idn-sub">
        If you lose your phone, each of these codes lets you sign in once. Keep them somewhere safe,
        away from this computer. They are not shown again.
      </p>
      <ul className="idn-codes" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c} className="idn-mono">
            {c}
          </li>
        ))}
      </ul>
      <div className="idn-row">
        <Button
          variant="secondary"
          leadingIcon={<Copy size={14} aria-hidden />}
          onClick={() => {
            void navigator.clipboard?.writeText(codes.join('\n')).then(() => setSaved(true));
          }}
        >
          Copy
        </Button>
        <Button variant="secondary" onClick={download}>
          Download .txt
        </Button>
      </div>
      <label className="idn-check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />I
        saved my recovery codes
      </label>
      <Button variant="primary" size="lg" block disabled={!saved} onClick={onDone}>
        Continue
      </Button>
    </div>
  );
}

function officeBadgeStyle(o: OfficeSummary): CSSProperties {
  return {
    '--office-color': `var(--chart-${(officeColorIndex(o.colorToken) % 8) + 1})`,
  } as CSSProperties;
}

function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function OfficeStep({ session, onDone }: { session: SessionInfo; onDone: () => void }) {
  const offices = session.context.offices;
  // The PMS of each office, as the top-bar switcher shows it (not the time zone).
  const [systems, setSystems] = useState<Map<string, string | null> | null>(null);
  useEffect(() => {
    let live = true;
    identityApi.officeSystems().then(
      (r) => live && setSystems(new Map(r.offices.map((o) => [o.id, o.pmsSystem]))),
      () => undefined, // the short name stands in
    );
    return () => {
      live = false;
    };
  }, []);
  const systemOf = (id: string) =>
    systems?.has(id) ? officeSystemLabel(systems.get(id) ?? null) : null;
  const initial: LocationScope =
    session.context.scope ??
    (offices[0] ? { kind: 'location', locationId: offices[0].id } : { kind: 'all' });
  const [choice, setChoice] = useState<LocationScope>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options: Array<{ scope: LocationScope; key: string }> = [
    ...(session.context.allowAll ? [{ scope: { kind: 'all' } as LocationScope, key: 'all' }] : []),
    ...offices.map((o) => ({
      scope: { kind: 'location', locationId: o.id } as LocationScope,
      key: o.id,
    })),
  ];
  const selectedKey = choice.kind === 'all' ? 'all' : choice.locationId;

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      await identityApi.updateContext({
        scope: choice,
        ...(choice.kind === 'location' ? { workingLocationId: choice.locationId } : {}),
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const move = (delta: number) => {
    const i = options.findIndex((o) => o.key === selectedKey);
    const next = options[(i + delta + options.length) % options.length];
    if (next) setChoice(next.scope);
  };

  const first = greetingName(session.user.displayName);
  return (
    <div className="idn-view">
      <h2 className="idn-title">
        {greeting()}, {first}
      </h2>
      <p className="idn-sub">
        Pick where you are working at {session.practice.name}. You can switch any time from the top
        bar.
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <div
        className="idn-offices"
        role="radiogroup"
        aria-label="Office"
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            move(1);
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            move(-1);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            void open();
          }
        }}
      >
        {options.map((o) => {
          const office =
            o.scope.kind === 'location' ? offices.find((x) => x.id === o.key) : undefined;
          const selected = o.key === selectedKey;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              className="idn-office"
              data-selected={selected ? '' : undefined}
              onClick={() => setChoice(o.scope)}
            >
              {office ? (
                <span className="idn-office__badge" style={officeBadgeStyle(office)}>
                  {officeInitials(office)}
                </span>
              ) : (
                <span className="idn-office__badge idn-office__badge--all">
                  <Layers size={15} aria-hidden />
                </span>
              )}
              <span className="idn-office__text">
                <span className="idn-office__name">{office ? office.name : 'All offices'}</span>
                <span className="idn-office__meta">
                  {office
                    ? (systemOf(office.id) ?? office.shortName)
                    : `${offices.length} offices combined, each shown separately`}
                </span>
              </span>
              <span className="idn-office__check" aria-hidden>
                {selected ? <Check size={12} /> : null}
              </span>
            </button>
          );
        })}
      </div>
      <Button variant="hero" size="lg" block loading={busy} onClick={() => void open()}>
        Open {PRODUCT.shortName}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Flow                                                                      */
/* ------------------------------------------------------------------------ */

export interface SignInFlowProps {
  initial: AuthState;
  /** Called once when the flow mounts (the gate keeps it on screen until finished). */
  onStart?: () => void;
  /** Called when the person is fully signed in and has chosen an office. */
  onFinished: () => void;
}

export function SignInFlow({ initial, onStart, onFinished }: SignInFlowProps) {
  const [step, setStep] = useState<SignInStep>(() => initialStep(initial));
  // A first sign-in (temporary password): its account step shows in the progress.
  const [first, setFirst] = useState(
    () => initial.kind === 'enroll' && Boolean(initial.peek.setupStep),
  );
  // The temporary password, kept in memory only until the new one is saved.
  const [temporary, setTemporary] = useState('');
  const startRef = useRef(onStart);
  useEffect(() => {
    startRef.current?.();
  }, []);
  const peekName =
    initial.kind === 'mfa' || initial.kind === 'enroll' ? (initial.peek.displayName ?? '') : '';
  const peekPractice =
    initial.kind === 'mfa' || initial.kind === 'enroll' ? (initial.peek.practiceName ?? '') : '';
  const [who, setWho] = useState({ name: peekName, practice: peekPractice });
  const [codes, setCodes] = useState<string[]>([]);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const restart = () => {
    void identityApi.logout().catch(() => undefined);
    setWho({ name: '', practice: '' });
    setTemporary('');
    setFirst(false);
    setStep('password');
  };

  /** Moves to the step a sign-in answer names, or on to the office. */
  const advance = (status: LoginStatus | string) => {
    const next = stepForStatus(status);
    if (next === 'change_password' || next === 'add_email') setFirst(true);
    if (next !== 'change_password') setTemporary('');
    if (next) setStep(next);
    else void toOffice();
  };

  const toOffice = async () => {
    try {
      const s = await identityApi.session();
      if (s.context.offices.length <= 1) {
        onFinished();
        return;
      }
      setSession(s);
      setStep('office');
    } catch (err) {
      setError(errorMessage(err));
      setStep('password');
    }
  };

  return (
    <AuthFrame>
      <Steps step={step} first={first} />
      {error && step === 'password' ? <Alert>{error}</Alert> : null}
      {step === 'password' ? (
        <PasswordStep
          onSignedIn={(r, password) => {
            setError(null);
            setWho({ name: r.displayName, practice: r.practiceName });
            // Kept only for "Choose your own password" (sent back once, as the current one).
            setTemporary(r.status === 'password_change_required' ? password : '');
            advance(r.status);
          }}
        />
      ) : null}
      {step === 'verify' ? (
        <VerifyStep
          name={who.name}
          practice={who.practice}
          onVerified={(status) => advance(status)}
          onRestart={restart}
        />
      ) : null}
      {step === 'change_password' ? (
        <ChangePasswordStep
          name={who.name}
          temporary={temporary}
          onSaved={advance}
          onRestart={restart}
        />
      ) : null}
      {step === 'add_email' ? (
        <AddEmailStep name={who.name} onSaved={advance} onRestart={restart} />
      ) : null}
      {step === 'enroll' ? (
        <SetupStep
          name={who.name}
          onRestart={restart}
          onCodeInstead={() => setStep('verify')}
          onConfirmed={(c) => {
            // A text or email method added to existing recovery codes shows none.
            if (c.length === 0) {
              void toOffice();
              return;
            }
            setCodes(c);
            setStep('recovery');
          }}
        />
      ) : null}
      {step === 'recovery' ? <RecoveryStep codes={codes} onDone={() => void toOffice()} /> : null}
      {step === 'office' && session ? <OfficeStep session={session} onDone={onFinished} /> : null}
      {step === 'verify' || step === 'enroll' || step === 'recovery' ? (
        <p className="idn-hint">
          <ShieldCheck size={13} aria-hidden /> {PRODUCT.shortName} never calls or texts you to ask
          for a code. Don’t share one with anyone.
        </p>
      ) : null}
    </AuthFrame>
  );
}

/**
 * The "fictional data" note is true only for the demo fixtures and the local
 * dev server. A production build serves real patient data and never shows it
 * (C-20, PROC-16, PO-14, PER-14).
 */
export function showFictionalDataNote(): boolean {
  return isDemoMode() || import.meta.env.DEV === true;
}
